import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Subject, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { PAYMENT_TAB_ID } from '@shared-constants';
import { ModalVariant } from '@shared-enums';
import { DOCUMENT_STATE } from '@shared-utils';

import { IPurchasePayments } from 'src/app/@interfaces/payments.interface';
import { IPurchasesInvoices } from 'src/app/@interfaces/purchaseInvoice.interface';
import { AccountsPayableService } from 'src/app/@pages/accounts-payable/accounts-payable.service';
import { PurchasesService } from 'src/app/@pages/purchases/purchases.service';
import { PayPurchasePdfService } from 'src/app/@shared/components/dialog/crud-purchase/pay-purchase/pay-purchase-pdf.service';
import { PaymentsPdfService } from 'src/app/@shared/components/dialog/crud-quotation/payments/payments-pdf.service';
import { PdfGeneratorService } from 'src/app/@shared/components/pdf-generator/pdf-generator.service';
import { PurchasePaymentModalComponent } from 'src/app/uplift/features/commercial/shared/components/purchase-payment-modal/purchase-payment-modal.component';

import { ModalService } from '../modal/modal.service';
import { ElectronicDocumentPdfService } from '../pdf/electronic-document-pdf.service';

@Injectable({
  providedIn: 'root',
})
export class AccountsPayableActionHandlerService implements OnDestroy {
  private router = inject(Router);
  private purchasesService = inject(PurchasesService);
  private accountsPayableService = inject(AccountsPayableService);
  private payPurchasePdfService = inject(PayPurchasePdfService);
  private paymentsPdfService = inject(PaymentsPdfService);
  private pdfGeneratorService = inject(PdfGeneratorService);
  private electronicDocumentPdfService = inject(ElectronicDocumentPdfService);
  private modalService = inject(ModalService);

  // Same pattern as Cartera: the page reloads its current listing on this
  // instead of being re-created, so search, filters and paging survive.
  private actionCompletedSource = new Subject<string>();
  readonly actionCompleted$ = this.actionCompletedSource.asObservable();

  private pendingPaymentModals = new Set<number>();

  // Not a root-lifecycle no-op: actionCompletedSource is a real long-lived
  // stream, so this still needs to complete it if this service is ever torn
  // down (e.g. in a test). It just never fires from Angular itself, since
  // providedIn: 'root' services live for the app's lifetime.
  ngOnDestroy(): void {
    this.actionCompletedSource.complete();
  }

  /** Call synchronously from the tap, before any await, and pass the result to handleAction. */
  openReceiptWindow(): Window | null {
    return this.pdfGeneratorService.openPendingWindow();
  }

  closeReceiptWindow(receiptWindow: Window | null | undefined): void {
    this.pdfGeneratorService.closePendingWindow(receiptWindow);
  }

  private async generateReport(
    purchase: IPurchasesInvoices,
    open: boolean,
    targetWindow: Window | null = null,
  ) {
    try {
      const fullPurchase = await firstValueFrom(
        this.purchasesService.getPurchaseInvoice(purchase.id_invoice!),
      );
      const paymentsDescriptions: string[] = (fullPurchase.payment ?? []).map(
        (payment: { description: string }) => payment.description,
      );

      return await this.payPurchasePdfService.createPaymentPurchasePdf(
        open,
        'Cancelled',
        fullPurchase,
        this.paymentsPdfService.getCompanyLogo(),
        paymentsDescriptions.join(', '),
        targetWindow,
      );
    } catch (error) {
      this.pdfGeneratorService.closePendingWindow(targetWindow);
      throw error;
    }
  }

  private configPayments(
    purchase: IPurchasesInvoices,
    invoicePayments: IPurchasePayments[],
    initialTabId: string = PAYMENT_TAB_ID.REGISTER,
  ): void {
    const idInvoice = purchase.id_invoice;
    if (idInvoice) {
      if (this.pendingPaymentModals.has(idInvoice)) {
        return;
      }
      this.pendingPaymentModals.add(idInvoice);
    }

    const releaseLock = () => {
      if (idInvoice) {
        this.pendingPaymentModals.delete(idInvoice);
      }
    };

    this.modalService
      .openComponent(
        PurchasePaymentModalComponent,
        { variant: ModalVariant.DIALOG },
        {
          idInvoice: idInvoice,
          idProvider: purchase.provider.id_provider,
          invoicePayments,
          initialTabId,
          onClose: async (result: {
            sended: boolean;
            refresh: boolean;
            notifyState: number | null;
          }) => {
            releaseLock();

            // One email per session, and only when a payment left the
            // purchase partially or fully paid. Closing without registering
            // anything, or after only deleting a payment, says nothing.
            if (
              result.notifyState === DOCUMENT_STATE.PARTIAL ||
              result.notifyState === DOCUMENT_STATE.PAID
            ) {
              const state = result.notifyState;
              const getPdf = await this.generateReport(purchase, false);
              getPdf?.getBase64((data: string) => {
                this.accountsPayableService
                  .notifyPurchasePaymentState(purchase.id_invoice!, state, data)
                  .subscribe({
                    error: (error) => {
                      console.error(
                        'Error sending the payment state email',
                        error,
                      );
                    },
                  });
              });
            }

            if (result.refresh === true) {
              this.actionCompletedSource.next('config-payments');
            }
          },
        },
      )
      .pipe(take(1))
      .subscribe();
  }

  private async viewInvoiceDetail(
    purchase: IPurchasesInvoices,
    companyId: string,
  ): Promise<void> {
    if (purchase.electronic_support?.cuds) {
      try {
        await this.electronicDocumentPdfService.openSupportDocumentPdf(
          purchase.id_invoice!,
        );
      } catch {
        // The service already alerts the user; swallow to avoid double-alert.
      }
    } else {
      this.router.navigate([
        '/details_purchase',
        purchase.id_invoice,
        companyId,
        1,
      ]);
    }
  }

  async handleAction(
    actionId: string,
    purchase: IPurchasesInvoices,
    invoicePayments: IPurchasePayments[],
    companyId: string,
    receiptWindow: Window | null = null,
  ): Promise<void> {
    switch (actionId) {
      case 'config-payments':
        this.configPayments(purchase, invoicePayments);
        break;

      case 'generate-report':
        await this.generateReport(purchase, true, receiptWindow);
        break;

      case 'view-invoice-detail':
        await this.viewInvoiceDetail(purchase, companyId);
        break;

      case 'view-invoice-detail-paid':
        this.configPayments(
          purchase,
          invoicePayments,
          PAYMENT_TAB_ID.INVOICE_DETAIL,
        );
        break;
    }
  }
}
